import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { BiddingBlockBidStatus } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import {
  BiddingApprovalState,
  BiddingBidSendErrorCategory,
  BiddingBidSendStatus,
  BiddingJepxReconcileOutcome,
  BiddingSpotBidStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { BiddingSpotApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

/** AC-BSP-130 の表のうち、 本画面が知らせる行。 */
export type SendResultNoticeKind =
  | 'retrying'
  | 'limitExceeded'
  | 'approvalFlowChanged'
  | 'jepxRejected'
  | 'memberInfoMissing'
  | 'workflowNotSent'
  | 'workflowJepxRejected'
  | 'deadlineUnreached'
  | 'reconcileBackToBidding'
  | 'reconcileDuplicate'
  | 'reconcileToAwaitingMatch'
  | 'reconcileWithdrawSent'

/** 通知の判定に使う申請セット (通常入札) ・グループ (ブロック入札) の状態。 */
export type SendResultSubject = {
  id: string
  targetDate?: ProtoDate
  /** 一覧 (polling) の承認軸・送信軸・原因分類。 */
  approvalState: BiddingApprovalState
  sendStatus: BiddingBidSendStatus
  sendErrorCategory: BiddingBidSendErrorCategory
  /** 一覧の JEPX 照合の結果 (AC-BSP-117)。 */
  jepxReconcileOutcome: BiddingJepxReconcileOutcome
  /** 明細 (screen 応答)。 明細に無い間は undefined。 */
  detail?: SendResultDetail
}

export type SendResultDetail = {
  approvalState: BiddingApprovalState
  sendStatus: BiddingBidSendStatus
  areaCodes: readonly AreaCode[]
  /** すべての札・leg が失効。 */
  allExpired: boolean
  /** 業務ワークフロー経由の札を含む。 */
  fromWorkflow: boolean
  /** 明細が持つ JEPX 照合の結果。 一覧のそれと揃うまで、 照合の通知の根拠 (札の内容・状態) が古い。 */
  jepxReconcileOutcome: BiddingJepxReconcileOutcome
}

/** 一覧の観測ごとに持ち越す、 1 つの申請セット・グループの観測。 */
export type SendObservation = {
  /** 送信軸 (予約中・送信待ち・送信中・再送待ち) にいたのを観測した。 */
  inFlight: boolean
  /**
   * 通信の不調による再送待ち (SEND_FAILED + TRANSIENT) を初めて観測した時刻 (ms)。
   * 送信軸を出るまで持ち越す。
   */
  failingSinceMs?: number
  retryNoticed: boolean
  approvalState: BiddingApprovalState
  sendStatus: BiddingBidSendStatus
  sendErrorCategory: BiddingBidSendErrorCategory
  /** 最後に下書きで観測した時の原因分類。 下書きを観測していなければ undefined。 */
  lastDraftCategory?: BiddingBidSendErrorCategory
  /**
   * 追いついた明細で「送信失敗の確定のまま、 札・leg が失効し切っていない」 を観測した印
   * (JEPX に届いたかを確かめている途中。 AC-BSP-117)。 3 軸が変わった時と、 失効を知らせた
   * 時に false へ戻る。 この印が立った後に失効し切った札だけが「確かめた結果、 届いていなかった」
   * 札で、 画面を開く前に失効していた札や、 失効まで一度に進んだ札には立たない。
   */
  failureUnsettled: boolean
}

/**
 * 再送待ちが続いた時に「送信に時間がかかっています」 を出すまでの時間 (AC-BSP-130 の
 * 「通信の不調が長引いている」 の条件は EN 判断範囲。 §エラーハンドリング「JEPX への送信」)。
 * 送信 worker が送り直しの滞留を運用ログに出す閾値 (`bidSendStalledThreshold`) と揃え、
 * 運用が気付くのと同じ時点で利用者にも知らせる。
 */
export const SEND_RETRY_NOTICE_AFTER_MS = 10 * 60 * 1000

const IN_FLIGHT_STATUSES: ReadonlySet<BiddingBidSendStatus> = new Set([
  BiddingBidSendStatus.RESERVED,
  BiddingBidSendStatus.ENQUEUED,
  BiddingBidSendStatus.SENDING,
  BiddingBidSendStatus.SEND_FAILED,
])

const REFUSED_TO_DRAFT: Partial<
  Record<BiddingBidSendErrorCategory, SendResultNoticeKind>
> = {
  [BiddingBidSendErrorCategory.INTERNAL_VALIDATION_VIOLATION]: 'limitExceeded',
  [BiddingBidSendErrorCategory.INTERNAL_VALIDATION_VIOLATION_APPROVAL_MODE]:
    'approvalFlowChanged',
}

const isRefusal = (category: BiddingBidSendErrorCategory): boolean =>
  REFUSED_TO_DRAFT[category] !== undefined

/**
 * 送信軸から下書きに戻った札の原因分類 → 行。 再検証の違反に加え、 JEPX が内容を理由に
 * 受け付けなかった札と、 JEPX 会員 ID 未登録で送れなかった札 (発注組織の未登録に分類される)
 * も下書きに戻す (AC-BSP-117 / AC-IJM-009)。 提出済から下書きに戻すのは送信 worker だけなので、
 * 下書きに残る原因分類で理由を読める。
 */
const RETURNED_TO_DRAFT: Partial<
  Record<BiddingBidSendErrorCategory, SendResultNoticeKind>
> = {
  ...REFUSED_TO_DRAFT,
  [BiddingBidSendErrorCategory.VALIDATION_REJECTED]: 'jepxRejected',
  [BiddingBidSendErrorCategory.ORDERING_ORGANIZATION_UNREGISTERED]:
    'memberInfoMissing',
}

/**
 * 締切で届かなかったと決まった送信の原因分類。 締切後の拒否も backend は締切超過
 * (GATE_CLOSED) に分類する (eSLJ は遅れた札と内容の誤りを同じ種類のエラーで返すため) ので、
 * 「締切後のため失効」 の行とは分けられず、 こちらの行で知らせる。
 */
const DEADLINE_UNREACHED_CATEGORIES: ReadonlySet<BiddingBidSendErrorCategory> =
  new Set([
    BiddingBidSendErrorCategory.GATE_CLOSED,
    BiddingBidSendErrorCategory.DELIVERY_CONTRACT_CODE_MISSING,
  ])

type SettledDecision =
  | { kind: SendResultNoticeKind }
  | { kind: undefined }
  | 'waitForDetail'

/**
 * 提出済・送信失敗の確定の札の、 明細から見た失効の進み具合。
 * - `notFailedFinal`: 提出済・送信失敗の確定ではない。
 * - `detailBehind`: 明細が一覧の承認軸・送信軸に追いついていない (札・leg の状態が古い)。
 * - `unsettled`: 追いついた明細で、 札・leg がまだ失効し切っていない (締切前の拒否か、
 *   JEPX に届いたかを確かめている途中。 AC-BSP-117)。
 * - `expired`: 追いついた明細で、 札・leg がすべて失効。
 */
export type FailedFinalOutcome =
  | 'notFailedFinal'
  | 'detailBehind'
  | 'unsettled'
  | 'expired'

export function failedFinalOutcomeOf(
  subject: SendResultSubject,
): FailedFinalOutcome {
  if (
    subject.approvalState !== BiddingApprovalState.SUBMITTED ||
    subject.sendStatus !== BiddingBidSendStatus.SEND_FAILED_FINAL
  ) {
    return 'notFailedFinal'
  }
  const { detail } = subject
  if (
    detail === undefined ||
    detail.approvalState !== subject.approvalState ||
    detail.sendStatus !== subject.sendStatus
  ) {
    return 'detailBehind'
  }
  return detail.allExpired ? 'expired' : 'unsettled'
}

/** 業務ワークフロー経由の札・グループか (workflow_id の有無。 通常入札・ブロック入札で共通)。 */
export const isWorkflowLinked = (workflowId: string | undefined): boolean =>
  workflowId !== undefined && workflowId !== ''

/**
 * 失効の決着を知った契機。
 * - `sendSettled`: 送信軸を出た観測で、 同時に失効し切っていた。
 * - `settledAfterDeadline`: 送信中のまま締切を迎え、 確かめている途中を観測した後に失効し切った。
 */
export type ExpiryTrigger = 'sendSettled' | 'settledAfterDeadline'

/**
 * 失効にした札・グループの行。
 * `settledAfterDeadline` は、 確かめた結果届いていなかったという事実で行 9 を選ぶ。 確かめた後の
 * 失効の分類は通信の不調由来でも VALIDATION_REJECTED になりうるので、 分類では選ばない
 * (ワークフロー経由かも問わない)。
 * `sendSettled` のワークフロー経由の札は、 原因分類で行 7 / 8 (締切超過などは行 9)。
 * ワークフロー経由でない札の失効は入札締切の時点か後だけ (締切前の拒否は下書きに戻す。
 * AC-BSP-117) なので、 分類を問わず行 9。
 */
export function expiredRowOf(
  category: BiddingBidSendErrorCategory,
  fromWorkflow: boolean,
  trigger: ExpiryTrigger,
): SendResultNoticeKind | undefined {
  if (trigger === 'settledAfterDeadline' || !fromWorkflow) {
    return 'deadlineUnreached'
  }
  // 確定済ワークフロー経由の札を再検証の違反・JEPX 会員 ID 未登録で送れずに失効にした時は、
  // 締切で失効にした時もワークフローの行で知らせる (AC-BSP-130)。
  if (
    isRefusal(category) ||
    category === BiddingBidSendErrorCategory.DELIVERY_CONTRACT_CODE_MISSING ||
    category === BiddingBidSendErrorCategory.ORDERING_ORGANIZATION_UNREGISTERED
  ) {
    return 'workflowNotSent'
  }
  if (category === BiddingBidSendErrorCategory.VALIDATION_REJECTED) {
    return 'workflowJepxRejected'
  }
  return DEADLINE_UNREACHED_CATEGORIES.has(category)
    ? 'deadlineUnreached'
    : undefined
}

/**
 * 送信軸を出た申請セット・グループの結果の行。 失効の判定は札・leg の状態を見るので、
 * 明細が一覧の送信状態に追いつくまで待つ。
 */
function decideSettled(subject: SendResultSubject): SettledDecision {
  const { detail } = subject
  if (subject.approvalState === BiddingApprovalState.DRAFT) {
    const kind = RETURNED_TO_DRAFT[subject.sendErrorCategory]
    if (kind === undefined) return { kind: undefined }
    return detail === undefined ? 'waitForDetail' : { kind }
  }
  const outcome = failedFinalOutcomeOf(subject)
  if (outcome === 'detailBehind') return 'waitForDetail'
  // 失効にしていない札は、 締切前の拒否 (下書きに戻す扱いは未実装) か、 JEPX に届いたかを
  // 確かめる対象 (AC-BSP-117) で、 本表のどの行にもまだ当たらない。
  if (outcome !== 'expired' || detail === undefined) return { kind: undefined }
  return {
    kind: expiredRowOf(
      subject.sendErrorCategory,
      detail.fromWorkflow,
      'sendSettled',
    ),
  }
}

const lastDraftCategoryOf = (
  previous: SendObservation | undefined,
  subject: SendResultSubject,
): BiddingBidSendErrorCategory | undefined =>
  subject.approvalState === BiddingApprovalState.DRAFT
    ? subject.sendErrorCategory
    : previous?.lastDraftCategory

const settledObservation = (
  previous: SendObservation | undefined,
  subject: SendResultSubject,
  failureUnsettled: boolean,
): SendObservation => ({
  inFlight: false,
  retryNoticed: false,
  approvalState: subject.approvalState,
  sendStatus: subject.sendStatus,
  sendErrorCategory: subject.sendErrorCategory,
  lastDraftCategory: lastDraftCategoryOf(previous, subject),
  failureUnsettled,
})

/** 承認申請から送信までの途中の承認軸 (送信の前に観測しうる状態)。 */
const APPROVAL_PATH_STATES: ReadonlySet<BiddingApprovalState> = new Set([
  BiddingApprovalState.PENDING,
  BiddingApprovalState.APPROVED,
])

/**
 * 前回の観測から、 送信の結果として下書き・失効に変わったと言えるか。 送信軸か提出済から
 * 変わった札は送信の結果。 承認待ちから一覧の観測の間に承認・送信まで終わった札は、
 * 提出済の結果になった時と、 最後に下書きで観測した時と違う原因分類で下書きに戻った時に
 * 限る。 下書きに戻した札の原因分類は残り続けるので、 利用者の承認申請→申請取消で前と同じ
 * 原因分類の下書きに戻っただけの札を、 送信の結果と取り違えないため (下書きを観測して
 * いない札は区別できないので知らせない)。 下書きのまま原因分類が変わった札は、 提出から
 * 送信までが一覧の観測の間に終わった札。
 */
const observedSending = (
  previous: SendObservation | undefined,
  subject: SendResultSubject,
): previous is SendObservation => {
  if (previous === undefined) return false
  const changed =
    previous.approvalState !== subject.approvalState ||
    previous.sendStatus !== subject.sendStatus ||
    previous.sendErrorCategory !== subject.sendErrorCategory
  if (!changed) return false
  if (
    previous.inFlight ||
    previous.approvalState === BiddingApprovalState.SUBMITTED ||
    previous.approvalState === BiddingApprovalState.DRAFT
  ) {
    return true
  }
  if (!APPROVAL_PATH_STATES.has(previous.approvalState)) return false
  if (subject.approvalState === BiddingApprovalState.SUBMITTED) return true
  return (
    subject.approvalState === BiddingApprovalState.DRAFT &&
    previous.lastDraftCategory !== undefined &&
    previous.lastDraftCategory !== subject.sendErrorCategory
  )
}

const isSameAxes = (
  previous: SendObservation,
  subject: SendResultSubject,
): boolean =>
  previous.approvalState === subject.approvalState &&
  previous.sendStatus === subject.sendStatus &&
  previous.sendErrorCategory === subject.sendErrorCategory

/**
 * 一覧の 1 回の観測で、 申請セット・グループの観測を進め、 知らせる行を返す
 * (AC-BSP-130)。 `observedAtMs` は一覧を取得した時刻。
 *
 * 画面を開く前に結果の出ていた札は遷移を見ていないので知らせない。 明細を待つ間は
 * 前回の観測を保ち、 次の観測で判定し直す。
 */
export function observeSendResult(
  previous: SendObservation | undefined,
  subject: SendResultSubject,
  observedAtMs: number,
): { next: SendObservation; notice?: SendResultNoticeKind } {
  if (IN_FLIGHT_STATUSES.has(subject.sendStatus)) {
    // 通信の不調で送り直している札だけ。 JEPX 会員情報の未登録で止まっている札
    // (受渡契約コード未設定に分類される) は、 登録されるまで送れないので「送れるまで
    // 送り直します」 とは言わず、 待った時間も通信の不調の長さに数えない。
    const retryingTransient =
      subject.sendStatus === BiddingBidSendStatus.SEND_FAILED &&
      subject.sendErrorCategory === BiddingBidSendErrorCategory.TRANSIENT
    const failingSinceMs =
      previous?.failingSinceMs ?? (retryingTransient ? observedAtMs : undefined)
    const dueRetryNotice =
      retryingTransient &&
      previous?.retryNoticed !== true &&
      failingSinceMs !== undefined &&
      observedAtMs - failingSinceMs >= SEND_RETRY_NOTICE_AFTER_MS &&
      subject.detail !== undefined
    const next: SendObservation = {
      inFlight: true,
      failingSinceMs,
      retryNoticed: previous?.retryNoticed === true || dueRetryNotice,
      approvalState: subject.approvalState,
      sendStatus: subject.sendStatus,
      sendErrorCategory: subject.sendErrorCategory,
      lastDraftCategory: previous?.lastDraftCategory,
      failureUnsettled: false,
    }
    return dueRetryNotice ? { next, notice: 'retrying' } : { next }
  }
  const outcome = failedFinalOutcomeOf(subject)
  const unsettled = outcome === 'unsettled'
  // observedSending の型述語は偽の枝で previous を狭めるので、 先に別名へ取る。
  const prior: SendObservation | undefined = previous
  if (!observedSending(previous, subject)) {
    if (prior !== undefined && isSameAxes(prior, subject)) {
      // 明細の遅れでは前回を保つ (確かめている途中の印を落とさない)。
      if (outcome === 'detailBehind') return { next: prior }
      // 確かめている途中を観測した後に失効し切った = 確かめた結果、 届いていなかった札。
      if (outcome === 'expired' && prior.failureUnsettled) {
        const kind = expiredRowOf(
          subject.sendErrorCategory,
          subject.detail?.fromWorkflow === true,
          'settledAfterDeadline',
        )
        const next = settledObservation(prior, subject, false)
        return kind === undefined ? { next } : { next, notice: kind }
      }
    }
    return { next: settledObservation(previous, subject, unsettled) }
  }
  const decision = decideSettled(subject)
  if (decision === 'waitForDetail') return { next: previous }
  const next = settledObservation(previous, subject, unsettled)
  return decision.kind === undefined
    ? { next }
    : { next, notice: decision.kind }
}

const RECONCILE_NOTICE: Partial<
  Record<BiddingJepxReconcileOutcome, SendResultNoticeKind>
> = {
  [BiddingJepxReconcileOutcome.BACK_TO_BIDDING]: 'reconcileBackToBidding',
  [BiddingJepxReconcileOutcome.TO_AWAITING_MATCH]: 'reconcileToAwaitingMatch',
  [BiddingJepxReconcileOutcome.DUPLICATE_ON_JEPX]: 'reconcileDuplicate',
  [BiddingJepxReconcileOutcome.WITHDRAW_SENT]: 'reconcileWithdrawSent',
}

/**
 * JEPX 照合の結果の行 (AC-BSP-130)。 画面を開いた最初の取得は知らせない。 以後の取得で、
 * 既にあったセットの値が変わった時と、 新しく現れたセット (`previous` が undefined) の値が
 * 未設定以外の時に知らせる。 照合で作ったセットは前回の取得に無いので後者が要る。
 */
export function observeReconcileOutcome(
  previous: BiddingJepxReconcileOutcome | undefined,
  current: BiddingJepxReconcileOutcome,
  firstFetch: boolean,
): SendResultNoticeKind | undefined {
  if (firstFetch || previous === current) return undefined
  return RECONCILE_NOTICE[current]
}

/** 一覧 (polling) の item のうち、 通知の判定に使う値。 */
type ListItemOf = {
  id: string
  targetDate?: ProtoDate
  approvalState: BiddingApprovalState
  sendStatus: BiddingBidSendStatus
  sendErrorCategory: BiddingBidSendErrorCategory
  jepxReconcileOutcome: BiddingJepxReconcileOutcome
}

const uniqueSorted = (codes: readonly AreaCode[]): AreaCode[] =>
  [...new Set(codes)].sort((a, b) => a - b)

function subjectsOf<Detail extends { id: string }>(
  items: readonly ListItemOf[],
  details: readonly Detail[],
  detailOf: (detail: Detail) => SendResultDetail,
): SendResultSubject[] {
  const detailById = new Map(details.map((d) => [d.id, d]))
  return items.map((item) => {
    const detail = detailById.get(item.id)
    return {
      id: item.id,
      targetDate: item.targetDate,
      approvalState: item.approvalState,
      sendStatus: item.sendStatus,
      sendErrorCategory: item.sendErrorCategory,
      jepxReconcileOutcome: item.jepxReconcileOutcome,
      detail: detail === undefined ? undefined : detailOf(detail),
    }
  })
}

/** 通常入札の一覧 item と明細 (申請セット) から通知の判定の入力を作る。 */
export function spotSendResultSubjects(
  items: readonly ListItemOf[],
  details: readonly BiddingSpotApprovalRequest[],
): SendResultSubject[] {
  return subjectsOf(items, details, (request) => ({
    approvalState: request.approvalState,
    sendStatus: request.sendStatus,
    areaCodes: uniqueSorted(request.spotBids.map((bid) => bid.areaCode)),
    allExpired:
      request.spotBids.length > 0 &&
      request.spotBids.every(
        (bid) => bid.bidStatus === BiddingSpotBidStatus.EXPIRED,
      ),
    fromWorkflow: request.spotBids.some((bid) =>
      isWorkflowLinked(bid.workflowId),
    ),
    jepxReconcileOutcome: request.jepxReconcileOutcome,
  }))
}

/** ブロック入札の一覧 item と明細 (グループ) から通知の判定の入力を作る。 */
export function blockSendResultSubjects(
  items: readonly ListItemOf[],
  details: readonly BiddingBlockApprovalRequest[],
): SendResultSubject[] {
  return subjectsOf(items, details, (request) => {
    const legs = [request.primary, request.secondary].filter(
      (leg) => leg !== undefined,
    )
    return {
      approvalState: request.approvalState,
      sendStatus: request.sendStatus,
      areaCodes: uniqueSorted(legs.map((leg) => leg.areaCode)),
      allExpired:
        legs.length > 0 &&
        legs.every((leg) => leg.bidStatus === BiddingBlockBidStatus.EXPIRED),
      fromWorkflow: isWorkflowLinked(request.workflowId),
      jepxReconcileOutcome: request.jepxReconcileOutcome,
    }
  })
}
